/**
 * conditionsStore.ts — shared forecast-time + forecast-point state (D3, D4).
 *
 * Module-level store exposed with the useSyncExternalStore shape
 * (subscribe / getSnapshot). Owns:
 *
 *   - forecastTime: the single hour index into the 72 h fetched window +
 *     its ISO label, shared by the map overlay and the Wind/Waves
 *     sections. Default = current hour (index 0). The Tide chart keeps
 *     its own 14-day navigation — untouched.
 *   - forecastPoint: explicit, pinnable lat/lon used by the Wind +
 *     Weather + Waves sections, with defaulting order:
 *         live GPS fix → map center → app fallback center
 *     A pinned point overrides all defaults until unpinned.
 *   - conditions point data (wind/weather/swell) with provenance, loaded
 *     via the windField point fetches (outside pins) or derived from the
 *     loaded area sample cells (normal centre and inside pins — v56 Task 6).
 *
 * Session-scope persistence (module state) is the minimum required by the
 * contract; per-user persistence (db.setSetting('conditionsPoint')) is
 * optional and intentionally not wired here.
 */

import {
  fetchForecastCells,
  fetchSwellPoint,
  fetchWeatherPoint,
  type ForecastCellsResult,
  type WindGridBounds,
  type WindFieldPointForecast,
} from '../lib/windField';
import { forecastCellSetKey, planViewportSampleCells } from '../lib/forecastCells';
import { derivePointConditionsFromArea, pointInsideBounds } from '../lib/viewportConditions';

export const FORECAST_WINDOW_HOURS = 72;

/** Matches the map's Kenya-area fallback viewport center (map.ts). */
export const APP_FALLBACK_CENTER = { lat: 0.5, lon: 37.9 };

export type ForecastPointSource = 'gps' | 'map-center' | 'pinned';

export interface ForecastPoint {
  lat: number;
  lon: number;
  source: ForecastPointSource;
  pinned: boolean;
}

export interface ForecastTime {
  /** Hour index into the 72 h fetched window (0 = current hour). */
  hourIndex: number;
  /** ISO hour label for that index. */
  iso: string;
}

export interface PointProvenance {
  lat: number;
  lon: number;
  label: string;
}

export interface PointCondition {
  data: WindFieldPointForecast | null;
  provenance: PointProvenance | null;
}

export interface ConditionsPointData {
  wind: PointCondition;
  weather: PointCondition;
  swell: PointCondition;
}

export interface ViewportConditionsData {
  /**
   * Weather + Wind viewport result, or null when the weather provider
   * failed for the current area. Marine failure never nulls it.
   */
  weather: ForecastCellsResult | null;
  /** Marine viewport result, or null when the marine provider failed. */
  swell: ForecastCellsResult | null;
  /**
   * Key of the sample-cell set this result was fetched for. A previous
   * result survives a refetch only when its key matches the requested
   * cells — old-region data is never moved onto a new coast.
   */
  sampleSetKey: string;
}

export interface ConditionsState {
  forecastTime: ForecastTime;
  forecastPoint: ForecastPoint;
  /** Raw inputs used for defaulting (null until first report). */
  gps: { lat: number; lon: number } | null;
  mapCenter: { lat: number; lon: number } | null;
  mapBounds: WindGridBounds | null;
  /** Viewport geographic span in km (longest extent) — drives the pill's regional fade (F1). */
  mapSpanKm: number | null;
  pointData: ConditionsPointData | null;
  viewportData: ViewportConditionsData | null;
  loading: boolean;
  error: string | null;
  viewportLoading: boolean;
  viewportError: string | null;
  /**
   * Per-section point errors: 'weather' covers Wind too (both derive from
   * the single weather response). Optional in the type so existing
   * whole-state test fixtures still compile; the store always sets them.
   * `error` mirrors the first failing section for backward compatibility.
   */
  pointErrors?: { weather: string | null; swell: string | null };
  /** Per-section viewport errors; `viewportError` mirrors the first one. */
  viewportErrors?: { weather: string | null; swell: string | null };
}

/**
 * The visible map centre is the default Conditions anchor. A live position
 * stays available for an explicit pin, but never silently replaces the map
 * area the user is looking at.
 */
export function resolveForecastPoint(
  _gps: { lat: number; lon: number } | null,
  mapCenter: { lat: number; lon: number } | null,
): ForecastPoint {
  if (mapCenter)
    return { lat: mapCenter.lat, lon: mapCenter.lon, source: 'map-center', pinned: false };
  return {
    lat: APP_FALLBACK_CENTER.lat,
    lon: APP_FALLBACK_CENTER.lon,
    source: 'map-center',
    pinned: false,
  };
}

// ── Module state ──

let windowStart: Date = (() => {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  return d;
})();

let forecastTime: ForecastTime = {
  hourIndex: 0,
  iso: windowStart.toISOString(),
};

let gps: { lat: number; lon: number } | null = null;
let mapCenter: { lat: number; lon: number } | null = null;
let mapBounds: WindGridBounds | null = null;
let mapSpanKm: number | null = null;
let forecastPoint: ForecastPoint = resolveForecastPoint(null, null);
let pointData: ConditionsPointData | null = null;
let viewportData: ViewportConditionsData | null = null;
let loading = false;
let error: string | null = null;
let viewportLoading = false;
let viewportError: string | null = null;
let pointErrors: { weather: string | null; swell: string | null } = { weather: null, swell: null };
let viewportErrors: { weather: string | null; swell: string | null } = {
  weather: null,
  swell: null,
};

let pointFetchSeq = 0;
let viewportFetchSeq = 0;

const listeners = new Set<() => void>();
let state: ConditionsState = buildState();

/** Restore defaults and drop listeners. Test support / logout hygiene. */
export function resetConditionsStore(): void {
  windowStart = (() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    return d;
  })();
  forecastTime = { hourIndex: 0, iso: windowStart.toISOString() };
  gps = null;
  mapCenter = null;
  mapBounds = null;
  mapSpanKm = null;
  forecastPoint = resolveForecastPoint(null, null);
  pointData = null;
  viewportData = null;
  loading = false;
  error = null;
  viewportLoading = false;
  viewportError = null;
  pointErrors = { weather: null, swell: null };
  viewportErrors = { weather: null, swell: null };
  pointFetchSeq = 0;
  viewportFetchSeq = 0;
  listeners.clear();
  state = buildState();
}

function buildState(): ConditionsState {
  return {
    forecastTime,
    forecastPoint,
    gps,
    mapCenter,
    mapBounds,
    mapSpanKm,
    pointData,
    viewportData,
    loading,
    error,
    viewportLoading,
    viewportError,
    pointErrors,
    viewportErrors,
  };
}

function notify(): void {
  state = buildState();
  for (const fn of listeners) fn();
}

const isoForHour = (hourIndex: number): string =>
  new Date(windowStart.getTime() + hourIndex * 3_600_000).toISOString();

// ── Store surface (useSyncExternalStore shape) ──

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Stable reference between mutations — safe for useSyncExternalStore. */
export function getSnapshot(): ConditionsState {
  return state;
}

// ── Actions ──

/** Align the store's hour window with a fetched field (C2 calls this). */
export function setForecastWindowStart(iso: string): void {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return;
  windowStart = d;
  forecastTime = { hourIndex: 0, iso: isoForHour(0) };
  notify();
}

/** Set the shared forecast hour (clamped to the 72 h window). */
export function setForecastTime(hourIndex: number): void {
  const clamped = Math.max(0, Math.min(FORECAST_WINDOW_HOURS - 1, Math.round(hourIndex)));
  if (clamped === forecastTime.hourIndex) return;
  forecastTime = { hourIndex: clamped, iso: isoForHour(clamped) };
  notify();
}

/** Live position for the explicit "pin to my location" action. */
export function setGPSFix(fix: { lat: number; lon: number } | null): void {
  gps = fix;
  notify();
}

/** Map centre report; re-defaults the point unless pinned. */
export function setMapCenter(center: { lat: number; lon: number } | null): void {
  mapCenter = center;
  if (!forecastPoint.pinned) forecastPoint = resolveForecastPoint(gps, mapCenter);
  notify();
}

/** Report the complete visible viewport in one update. */
export function setMapViewport(input: {
  center: { lat: number; lon: number };
  bounds: WindGridBounds;
  spanKm: number;
}): void {
  mapCenter = input.center;
  mapBounds = input.bounds;
  mapSpanKm = Number.isFinite(input.spanKm) ? Math.max(0, input.spanKm) : null;
  if (!forecastPoint.pinned) forecastPoint = resolveForecastPoint(gps, mapCenter);
  notify();
}

/**
 * Viewport geographic span report (km, longest extent — see lib/pillFade).
 * Drives the Conditions pill's regional fade; never touches the forecast
 * point or point data. The pill is a summary of the point, so span only
 * decides whether that summary is shown.
 */
export function setMapSpanKm(km: number | null): void {
  const clamped = km != null && Number.isFinite(km) ? Math.max(0, km) : null;
  if (clamped === mapSpanKm) return;
  mapSpanKm = clamped;
  notify();
}

/**
 * Explicit user-set forecast point. Defaults to pinned (a user-chosen
 * location must survive GPS/map updates); pass {pinned: false} for a
 * transient programmatic set.
 */
export function setForecastPoint(lat: number, lon: number, opts?: { pinned?: boolean }): void {
  forecastPoint = { lat, lon, source: 'pinned', pinned: opts?.pinned ?? true };
  notify();
}

/** Pin the forecast point to an explicit location. */
export function pinForecastPoint(lat: number, lon: number): void {
  setForecastPoint(lat, lon, { pinned: true });
}

/** Release the pin; the point returns to the visible map centre. */
export function unpinForecastPoint(): void {
  if (!forecastPoint.pinned) return;
  forecastPoint = resolveForecastPoint(gps, mapCenter);
  notify();
}

/** Empty point-data shell used when one section resolves before the other. */
const EMPTY_POINT_DATA: ConditionsPointData = {
  wind: { data: null, provenance: null },
  weather: { data: null, provenance: null },
  swell: { data: null, provenance: null },
};

const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/**
 * Serve the current forecast point from the loaded area cells (v56 Task 6).
 * Applies only when the loaded viewport result matches the CURRENT
 * requested area (same sample-set key) and the point lies inside it —
 * old-region cells never feed a new-area point, and an outside pin keeps
 * its point-path data. Sections whose kind has no loaded cells become
 * null so stale point-path data never survives a real area change.
 * Returns true when a change was applied (callers notify).
 */
function derivePointDataFromArea(): boolean {
  const cells = viewportData;
  if (!cells || !mapBounds) return false;
  const currentKey = forecastCellSetKey(planViewportSampleCells(mapBounds).cells);
  if (cells.sampleSetKey !== currentKey) return false; // loaded cells are for a different area
  if (!pointInsideBounds(forecastPoint, mapBounds)) return false; // pinned outside
  const derived = derivePointConditionsFromArea({
    point: forecastPoint,
    weatherCells: cells.weather?.points ?? null,
    swellCells: cells.swell?.points ?? null,
  });
  pointData = {
    ...(pointData ?? EMPTY_POINT_DATA),
    wind: derived.wind,
    weather: derived.weather,
    swell: derived.swell,
  };
  if (derived.wind.data || derived.weather.data || derived.swell.data) {
    loading = false;
    error = null;
    pointErrors = { weather: null, swell: null };
  }
  return true;
}

/**
 * Fetch weather + swell point data at the current forecast point. The
 * weather response supplies BOTH Wind and Weather (no separate wind
 * request — v56 Task 4). Results are tagged with the point they were
 * fetched for and discarded if a newer trigger supersedes them.
 * Weather and marine settle independently (v56 Task 5): a failure keeps
 * last-known data for its own section and never touches the other side.
 *
 * v56 Task 6: the visible area serves the normal point. An unpinned point
 * (the map centre) and a pin inside the current bounds read from the
 * loaded area cells — never a point request. Derivation happens as soon
 * as the matching area result is in; until then there is nothing to show
 * and nothing to fetch. Only a pin OUTSIDE the loaded area runs the
 * two-request point path below.
 */
export async function triggerPointFetch(): Promise<void> {
  const pt = forecastPoint;
  if (!pt.pinned) {
    // v56 Task 6: the visible area serves the normal point. The map
    // centre is always inside the requested area; derive from the loaded
    // area cells as soon as they match the current area, and never start
    // a point request for it.
    if (derivePointDataFromArea()) notify();
    return;
  }
  // A pinned point reuses the loaded area cells when the area covers it.
  // Otherwise — no area loaded yet, or the pin lies outside the loaded
  // area — it runs the two-request point path.
  if (derivePointDataFromArea()) {
    notify();
    return;
  }
  const seq = ++pointFetchSeq;
  loading = true;
  error = null;
  pointErrors = { weather: null, swell: null };
  notify();
  const [weatherRes, swellRes] = await Promise.allSettled([
    fetchWeatherPoint(pt.lat, pt.lon),
    fetchSwellPoint(pt.lat, pt.lon),
  ]);
  if (seq !== pointFetchSeq) return; // superseded by a newer trigger
  if (weatherRes.status === 'fulfilled') {
    const weather = weatherRes.value;
    pointData = {
      ...(pointData ?? EMPTY_POINT_DATA),
      wind: {
        data: weather,
        provenance: { lat: weather.lat, lon: weather.lon, label: 'nearest wind forecast cell' },
      },
      weather: {
        data: weather,
        provenance: { lat: weather.lat, lon: weather.lon, label: 'nearest weather forecast cell' },
      },
    };
  } else {
    pointErrors.weather = errorMessage(weatherRes.reason);
  }
  if (swellRes.status === 'fulfilled') {
    const swell = swellRes.value;
    pointData = {
      ...(pointData ?? EMPTY_POINT_DATA),
      swell: {
        data: swell,
        provenance: { lat: swell.lat, lon: swell.lon, label: 'nearest marine model cell' },
      },
    };
  } else {
    pointErrors.swell = errorMessage(swellRes.reason);
  }
  error = pointErrors.weather ?? pointErrors.swell;
  loading = false;
  notify();
}

/**
 * Load the visible area's weather and swell cells for exceptions and map
 * arrows. Each provider settles independently: a failed weather request
 * keeps marine (and vice versa), and a previous result survives only when
 * its sampleSetKey matches the currently requested cells — data from a
 * different region is cleared so a new coast never reuses old arrows.
 */
export async function triggerViewportFetch(): Promise<void> {
  const bounds = mapBounds;
  if (!bounds) return;
  const seq = ++viewportFetchSeq;
  const sampleSetKey = forecastCellSetKey(planViewportSampleCells(bounds).cells);
  viewportLoading = true;
  viewportError = null;
  viewportErrors = { weather: null, swell: null };
  notify();
  const [weatherRes, swellRes] = await Promise.allSettled([
    fetchForecastCells({
      bounds,
      kind: 'weather',
      forecastTime: forecastTime.iso,
      sampling: 'viewport-summary',
    }),
    fetchForecastCells({
      bounds,
      kind: 'swell',
      forecastTime: forecastTime.iso,
      sampling: 'viewport-summary',
    }),
  ]);
  if (seq !== viewportFetchSeq) return;
  let next: ViewportConditionsData =
    viewportData?.sampleSetKey === sampleSetKey
      ? viewportData // same area — keep the previous result as the base
      : { weather: null, swell: null, sampleSetKey }; // different area — clean slate
  if (weatherRes.status === 'fulfilled') {
    if (!weatherRes.value.stale) next = { ...next, weather: weatherRes.value };
  } else {
    viewportErrors.weather = errorMessage(weatherRes.reason);
  }
  if (swellRes.status === 'fulfilled') {
    if (!swellRes.value.stale) next = { ...next, swell: swellRes.value };
  } else {
    viewportErrors.swell = errorMessage(swellRes.reason);
  }
  viewportData = next;
  // v56 Task 6: as soon as a matching area result is in, serve the normal
  // Conditions point from its cells (no point requests). The derive gate
  // skips outside pins and mismatched sample-set keys.
  derivePointDataFromArea();
  viewportError = viewportErrors.weather ?? viewportErrors.swell;
  viewportLoading = false;
  notify();
}
